import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext, createSelector } from '@ngxs/store';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { PulseService } from 'ng2-app/pulse/pulse.service';
import { normalizeRealTimeScores, normalizeHistoricalScores } from 'ng2-app/pulse/pulse.utils';
import { LenderUserRealTimeScores, LenderUserHistoricalScores, HistoricalScore } from 'ng2-app/models/pulse/scores.model';
import { GetScoresByLenderUserId, GetHistoricalScoresByGroupForUser } from 'ng2-app/store/pulse/pulse.action';
import { PulseHistoricalParams } from 'ng2-app/models/pulse/pulse.type';
import {
  QueryStateBase,
  setQueryLoading,
  setQuerySuccess,
  setQueryError,
  isQueryLoading,
  isQueryFetching,
} from 'ng2-app/store/query-state.model';

export interface IndividualScoreGroup {
  realTimeScores: LenderUserRealTimeScores | null;
  historicalScores: LenderUserHistoricalScores[];
  historicalOverrides: Record<number, Record<string, HistoricalScore>>;
}

export type IndividualLastSync = {
  realTimeScores: Date | null;
  historicalScores: Date | null;
};

export interface IndividualQueryEntry extends QueryStateBase<IndividualScoreGroup> {
  lastSync: IndividualLastSync;
  lastHistoricalParams?: PulseHistoricalParams;
}

export interface PulseIndividualStateModel {
  queries: { [lenderUserId: number]: IndividualQueryEntry };
}

const DEFAULT_DATA: IndividualScoreGroup = { realTimeScores: null, historicalScores: [], historicalOverrides: {} };
export const INDIVIDUAL_REALTIME_TTL = 1000 * 60 * 5; // 5 minutes

function createIndividualQuery(): IndividualQueryEntry {
  return {
    data: { ...DEFAULT_DATA, historicalScores: [] },
    status: 'idle',
    isFetching: false,
    error: null,
    lastSync: { realTimeScores: null, historicalScores: null },
  };
}

function isSameDay(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate() &&
    now.getHours() > 3 // Cron for historical scores runs at 2am
  );
}

@State<PulseIndividualStateModel>({
  name: 'pulseIndividual',
  defaults: {
    queries: {},
  },
})
@Injectable()
export class PulseIndividualState {
  constructor(private pulseService: PulseService) {}

  // --- Selectors ---

  @Selector()
  static queries(state: PulseIndividualStateModel) {
    return state?.queries ?? null;
  }

  static individualQuery(lenderUserId: number) {
    return createSelector(
      [PulseIndividualState.queries],
      (queries: PulseIndividualStateModel['queries']) =>
        queries[lenderUserId] ?? createIndividualQuery()
    );
  }

  static individualData(lenderUserId: number) {
    return createSelector(
      [PulseIndividualState.individualQuery(lenderUserId)],
      (query: IndividualQueryEntry) => query.data
    );
  }

  static individualIsLoading(lenderUserId: number) {
    return createSelector(
      [PulseIndividualState.individualQuery(lenderUserId)],
      (query: IndividualQueryEntry) => isQueryLoading(query)
    );
  }

  static individualIsFetching(lenderUserId: number) {
    return createSelector(
      [PulseIndividualState.individualQuery(lenderUserId)],
      (query: IndividualQueryEntry) => isQueryFetching(query)
    );
  }

  static individualLastSync(lenderUserId: number) {
    return createSelector(
      [PulseIndividualState.individualQuery(lenderUserId)],
      (query: IndividualQueryEntry) => query.lastSync.realTimeScores
    );
  }

  // --- Actions ---

  @Action(GetScoresByLenderUserId)
  getScoresByLenderUserId(
    ctx: StateContext<PulseIndividualStateModel>,
    { lenderUserId, overrideCache = false, historicalParams }: GetScoresByLenderUserId
  ): Observable<void> {
    const state = ctx.getState();
    const query = state?.queries[lenderUserId] ?? createIndividualQuery();

    const needsRealTime = !this.isRealTimeCacheValid(query, overrideCache);
    const needsHistorical = !this.isHistoricalCacheValid(query, historicalParams);

    if (!needsRealTime && !needsHistorical) {
      return of(undefined);
    }

    // Set loading state
    ctx.patchState({
      queries: {
        ...state?.queries,
        [lenderUserId]: { ...query, ...setQueryLoading(query) },
      },
    });

    const realTimeScores$ = needsRealTime
      ? this.pulseService.getRealTimeScoresByLenderUserId(lenderUserId)
      : of(query.data.realTimeScores);

    const historicalScores$ = needsHistorical
      ? this.pulseService.getHistoricalScoresByLenderUserId(lenderUserId, historicalParams)
      : of(query.data.historicalScores);

    return forkJoin({ realTimeScores: realTimeScores$, historicalScores: historicalScores$ }).pipe(
      tap(({ realTimeScores, historicalScores }) => {
        if (realTimeScores) normalizeRealTimeScores(realTimeScores);
        normalizeHistoricalScores(historicalScores as LenderUserHistoricalScores[]);
        const now = new Date();
        const currentQuery = ctx.getState().queries[lenderUserId] ?? createIndividualQuery();
        const data: IndividualScoreGroup = {
          realTimeScores,
          historicalScores: historicalScores as LenderUserHistoricalScores[],
          historicalOverrides: needsHistorical ? {} : currentQuery.data.historicalOverrides,
        };
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [lenderUserId]: {
              ...currentQuery,
              ...setQuerySuccess(data),
              lastSync: {
                realTimeScores: needsRealTime ? now : currentQuery.lastSync.realTimeScores,
                historicalScores: needsHistorical ? now : currentQuery.lastSync.historicalScores,
              },
              lastHistoricalParams: needsHistorical ? historicalParams : currentQuery.lastHistoricalParams,
            },
          },
        });
      }),
      catchError(error => this.patchQueryError(ctx, lenderUserId, error)),
      map(() => undefined)
    );
  }

  @Action(GetHistoricalScoresByGroupForUser)
  getHistoricalScoresByGroupForUser(
    ctx: StateContext<PulseIndividualStateModel>,
    { lenderUserId, scoringGroupId, historicalParams }: GetHistoricalScoresByGroupForUser
  ): Observable<LenderUserHistoricalScores[]> {
    const params: PulseHistoricalParams = {
      excludeZeroScores: historicalParams?.excludeZeroScores ?? false,
      fromDate: historicalParams?.fromDate,
      toDate: historicalParams?.toDate,
      scoringGroupId,
    };

    const state = ctx.getState();
    const query = state?.queries[lenderUserId] ?? createIndividualQuery();
    ctx.patchState({
      queries: { ...state?.queries, [lenderUserId]: { ...query, isFetching: true } },
    });

    return this.pulseService.getHistoricalScoresByLenderUserId(lenderUserId, params).pipe(
      tap(historicalScores => {
        normalizeHistoricalScores(historicalScores);
        const groupScores: Record<string, HistoricalScore> = {};
        for (const entry of historicalScores) {
          const group = entry.scoresByGroup.find(g => g.groupId === scoringGroupId);
          if (group) {
            Object.assign(groupScores, group.historicalScores);
          }
        }
        const currentQuery = ctx.getState().queries[lenderUserId] ?? createIndividualQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [lenderUserId]: {
              ...currentQuery,
              isFetching: false,
              data: {
                ...currentQuery.data,
                historicalOverrides: {
                  ...currentQuery.data.historicalOverrides,
                  [scoringGroupId]: groupScores,
                },
              },
            },
          },
        });
      }),
      catchError(error => this.patchQueryError(ctx, lenderUserId, error))
    );
  }

  // --- Error helper ---

  private patchQueryError(
    ctx: StateContext<PulseIndividualStateModel>,
    lenderUserId: number,
    error: unknown
  ): Observable<never> {
    const currentQuery = ctx.getState().queries[lenderUserId] ?? createIndividualQuery();
    ctx.patchState({
      queries: {
        ...ctx.getState().queries,
        [lenderUserId]: { ...currentQuery, ...setQueryError(error) },
      },
    });
    return throwError(() => error);
  }

  // --- Cache helpers ---

  private isRealTimeCacheValid(query: IndividualQueryEntry, forceRefresh: boolean): boolean {
    if (forceRefresh) return false;
    const lastSync = query.lastSync.realTimeScores;
    if (!lastSync) return false;
    return lastSync.getTime() + INDIVIDUAL_REALTIME_TTL > Date.now();
  }

  private isHistoricalCacheValid(query: IndividualQueryEntry, params?: PulseHistoricalParams): boolean {
    const lastSync = query.lastSync.historicalScores;
    if (!lastSync) return false;
    if (!this.historicalParamsMatch(query.lastHistoricalParams, params)) return false;
    return isSameDay(lastSync);
  }

  private historicalParamsMatch(a?: PulseHistoricalParams, b?: PulseHistoricalParams): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.excludeZeroScores === b.excludeZeroScores
      && a.fromDate === b.fromDate
      && a.toDate === b.toDate
      && a.scoringGroupId === b.scoringGroupId;
  }
}
